import { supabase } from '@/integrations/supabase/client';

// IMPORTANT: These must exactly mirror the Postgres enum "action_type"
// used by action_log.action and add_action_log(p_action).
type ActionType =
  | 'log_in'
  | 'log_out'
  | 'approve_user_request'
  | 'reject_user_request'
  | 'set_user_permission'
  | 'deleted_user'
  | 'suspended_user'
  | 'added_event'
  | 'deleted_event'
  | 'media_tagged'
  | 'media_untagged'
  | 'media_archived'
  | 'media_made_public'
  | 'media_made_private';

/**
 * Logs a user management action to the database using the add_action_log function
 */
async function logAction(userId: string, action: ActionType, content: string) {
  try {
    const { error } = await supabase
      .rpc('add_action_log', {
        p_user_id: userId,
        p_action: action,
        p_content: content
      });
    
    if (error) throw error;
  } catch (error) {
    console.error('Error logging action:', error);
  }
}

/**
 * Logs when a user's permission/role is changed
 */
export async function logPermissionChange(userId: string, newRole: string, performedBy: string) {
  await logAction(
    userId,
    'set_user_permission',
    `Role changed to ${newRole} for user ${userId} by ${performedBy}`
  );
}

/**
 * Logs when a user is approved
 */
export async function logUserApproval(userId: string, performedBy: string) {
  await logAction(
    userId,
    'approve_user_request',
    `User ${userId} approved by ${performedBy}`
  );
}

/**
 * Logs when a user is rejected
 */
export async function logUserRejection(userId: string, performedBy: string) {
  await logAction(
    userId,
    'reject_user_request',
    `User ${userId} rejected by ${performedBy}`
  );
}

/**
 * Logs when a user's suspension status is changed
 */
export async function logUserSuspension(userId: string, isSuspended: boolean, performedBy: string) {
  await logAction(
    userId,
    'suspended_user',
    `User ${userId} ${isSuspended ? 'suspended' : 'unsuspended'} by ${performedBy}`
  );
}

/**
 * Logs when a user is deleted
 */
export async function logUserDeletion(userId: string, performedBy: string) {
  await logAction(
    userId,
    'deleted_user',
    `User ${userId} deleted by ${performedBy}`
  );
}

// Helper functions for specific actions
export const logUserLogin = async (userId: string) => {
  await logAction(
    userId,
    'log_in',
    `User ${userId} logged in`
  );
};

export const logUserLogout = async (userId: string) => {
  await logAction(
    userId,
    'log_out',
    `User ${userId} logged out`
  );
};

export const logEventCreation = async (userId: string, eventId: string) => {
  await logAction(
    userId,
    'added_event',
    `User ${userId} added event ${eventId}`
  );
};

export const logEventDeletion = async (userId: string, eventId: string) => {
  await logAction(
    userId,
    'deleted_event',
    `User ${userId} deleted event ${eventId}`
  );
}; 

// ---------------------------------------------------------------------------
// Media-related helpers
// ---------------------------------------------------------------------------

const buildMediaLabel = (mediaId: string, mediaName?: string | null) => {
  const base = mediaName && mediaName.trim().length > 0 ? mediaName : mediaId;
  return `"${base}" (${mediaId})`;
};

/**
 * Log that media was tagged with one or more albums.
 */
export const logMediaTagged = async (
  performedBy: string,
  mediaId: string,
  mediaName: string | null | undefined,
  albumNames: string[],
) => {
  const label = buildMediaLabel(mediaId, mediaName);
  const albumsPart =
    albumNames && albumNames.length > 0
      ? ` with album(s): ${albumNames.join(', ')}`
      : '';

  await logAction(
    performedBy,
    'media_tagged',
    `Media ${label} tagged${albumsPart} by user ${performedBy}`,
  );
};

/**
 * Log that a tag was removed from media.
 */
export const logMediaUntagged = async (
  performedBy: string,
  mediaId: string,
  mediaName: string | null | undefined,
  albumName?: string,
) => {
  const label = buildMediaLabel(mediaId, mediaName);
  const albumPart = albumName ? ` from album "${albumName}"` : '';

  await logAction(
    performedBy,
    'media_untagged',
    `Media ${label} untagged${albumPart} by user ${performedBy}`,
  );
};

/**
 * Log that media was archived (deleted from the library).
 */
export const logMediaArchived = async (
  performedBy: string,
  mediaId: string,
  mediaName: string | null | undefined,
) => {
  const label = buildMediaLabel(mediaId, mediaName);

  await logAction(
    performedBy,
    'media_archived',
    `Media ${label} archived by user ${performedBy}`,
  );
};

/**
 * Log that media visibility was changed to public/private.
 */
export const logMediaVisibilityChanged = async (
  performedBy: string,
  mediaId: string,
  mediaName: string | null | undefined,
  makePublic: boolean,
) => {
  const label = buildMediaLabel(mediaId, mediaName);

  await logAction(
    performedBy,
    makePublic ? 'media_made_public' : 'media_made_private',
    `Media ${label} marked ${makePublic ? 'PUBLIC' : 'PRIVATE'} by user ${performedBy}`,
  );
};